import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CommonButton } from '@/components/ui/common-button';
import { CommonSelect } from '@/components/ui/common-select';
import { DateTimeInput } from '@/components/ui/DateTimeInput';
import { FullScreenLoader, TableBlurLoader } from '@/components/ui/loader';
import Pagination from '@/components/ui/pagination';
import { useToast } from '@/components/ui/use-toast';
import { usePagination } from '@/hooks/usePagination';
import reportService from '@/services/reportService';
import masterDataService from '@/services/masterDataService';
import TripReportTable from './TripReportTable';

const TripReport = () => {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentPage, pageSize, setPage, setPageSize } = usePagination({ defaultPage: 1, defaultPageSize: 100 });
  const [loading, setLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [reports, setReports] = useState([]);
  const [summary, setSummary] = useState(null);
  const [totalReports, setTotalReports] = useState(0);
  const [filters, setFilters] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      start_date: params.get('start_date') || '',
      end_date: params.get('end_date') || '',
      trip_status: params.get('trip_status') || '',
      port_ids: params.get('port_ids') || '',
      transporter_ids: params.get('transporter_ids') || '',
      include_summary: true
    };
  });

  const [errors, setErrors] = useState({
    start_date: '',
    end_date: ''
  });

  const [masterData, setMasterData] = useState({
    ports: [],
    transporters: []
  });
  const [activeDropdown, setActiveDropdown] = useState(null);

  useEffect(() => {
    if (!searchParams.get('page')) {
      const params = new URLSearchParams(searchParams);
      params.set('page', '1');
      setSearchParams(params, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const loadMasterData = async () => {
      setLoading(true);
      try {
        const ports = await masterDataService.getPorts(0, 500);
        const transporters = await masterDataService.getTransporters(0, 500, false);
        const portsData = Array.isArray(ports) ? ports : (ports.data || []);
        const transportersData = Array.isArray(transporters) ? transporters : (transporters.data || []);

        setMasterData({
          ports: portsData,
          transporters: transportersData
        });
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to load filter options. Please try again.",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
        setIsInitialLoad(false);
      }
    };

    loadMasterData();
  }, [toast]);




  useEffect(() => {
    if (filters.start_date && filters.end_date) {
      handleGenerateReport(true);
    }
  }, [
    currentPage,
    pageSize,
    filters.start_date,
    filters.end_date,
    filters.trip_status,
    filters.port_ids,
    filters.transporter_ids
  ]);

  const updateUrlWithFilters = (newFilters) => {
    const params = new URLSearchParams(searchParams);

    if (newFilters.start_date) params.set('start_date', newFilters.start_date);
    else params.delete('start_date');

    if (newFilters.end_date) params.set('end_date', newFilters.end_date);
    else params.delete('end_date');

    if (newFilters.trip_status) params.set('trip_status', newFilters.trip_status);
    else params.delete('trip_status');

    if (newFilters.port_ids) params.set('port_ids', newFilters.port_ids);
    else params.delete('port_ids');

    if (newFilters.transporter_ids) params.set('transporter_ids', newFilters.transporter_ids);
    else params.delete('transporter_ids');

    params.set('page', '1');

    setSearchParams(params);
  };

  const portOptions = masterData.ports
    .filter(port => port && port.id)
    .map(port => ({
      value: port.id.toString(),
      label: `${port.port_name} (${port.port_code})`
    }));

  const transporterOptions = masterData.transporters
    .filter(transporter => transporter && transporter.id)
    .map(transporter => ({
      value: transporter.id.toString(),
      label: transporter.company_name
    }));

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    updateUrlWithFilters(newFilters);

    if (errors[key]) {
      setErrors(prev => ({
        ...prev,
        [key]: ''
      }));
    }
  };

  const handleDateChange = (key, value) => {
    if (value) {
      const datePart = value.split('T')[0];
      handleFilterChange(key, datePart);
    } else {
      handleFilterChange(key, '');
    }

    if (errors[key]) {
      setErrors(prev => ({
        ...prev,
        [key]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!filters.start_date) {
      newErrors.start_date = "Start date is required";
    }

    if (!filters.end_date) {
      newErrors.end_date = "End date is required";
    }

    console.log("[TripReport] Validation errors:", newErrors);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleGenerateReport = async (isAutoGenerate = false) => {
    console.log(`[TripReport] handleGenerateReport called (isAutoGenerate: ${isAutoGenerate})`, filters);
    
    if (isAutoGenerate && (!filters.start_date || !filters.end_date)) {
      console.warn("[TripReport] Auto-generate skipped: missing dates.");
      return;
    }

    if (!isAutoGenerate && !validateForm()) {
      console.warn("[TripReport] Validation failed, aborting manual report generation.");
      return;
    }

    setGenerating(true);
    try {
      const apiFilters = { ...filters };
      if (apiFilters.port_ids) {
        apiFilters.port_ids = apiFilters.port_ids.split(',').map(id => id.trim()).filter(id => id);
      }

      if (apiFilters.transporter_ids) {
        apiFilters.transporter_ids = apiFilters.transporter_ids.split(',').map(id => id.trim()).filter(id => id);
      }

      if (apiFilters.driver_ids) {
        apiFilters.driver_ids = apiFilters.driver_ids.split(',').map(id => id.trim()).filter(id => id);
      }

      if (apiFilters.vehicle_ids) {
        apiFilters.vehicle_ids = apiFilters.vehicle_ids.split(',').map(id => id.trim()).filter(id => id);
      }

      if (apiFilters.trip_status) {
        apiFilters.trip_status = apiFilters.trip_status.split(',').map(status => status.trim()).filter(status => status);
      }

      let calculatedSkip = (currentPage - 1) * pageSize;
      let calculatedLimit = pageSize;

      if (isAutoGenerate) {
        const urlPage = parseInt(searchParams.get('page'), 10) || 1;
        const urlSize = parseInt(searchParams.get('size'), 10) || 100;
        calculatedSkip = (urlPage - 1) * urlSize;
        calculatedLimit = urlSize;
      }

      apiFilters.skip = calculatedSkip;
      apiFilters.limit = calculatedLimit;

      const response = await reportService.getTripReports(apiFilters);

      const formattedReports = reportService.formatTripReportData(response.data || []);
      setReports(formattedReports);

      if (response.total !== undefined) {
        setTotalReports(response.total);
      } else {
        setTotalReports(apiFilters.skip + formattedReports.length + (formattedReports.length === pageSize ? 1 : 0));
      }

      if (response.summary) {
        setSummary(response.summary);
      }

      if (!isAutoGenerate) {
        toast({
          title: "Success",
          description: `Generated report with ${formattedReports.length} trips.`
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to generate report: ${error.message || 'Please try again.'}`,
        variant: "destructive"
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleExportReport = async (format) => {
    setExporting(true);
    try {
      const apiFilters = { ...filters };

      if (apiFilters.port_ids) {
        apiFilters.port_ids = apiFilters.port_ids.split(',').map(id => id.trim()).filter(id => id);
      }

      if (apiFilters.transporter_ids) {
        apiFilters.transporter_ids = apiFilters.transporter_ids.split(',').map(id => id.trim()).filter(id => id);
      }

      if (apiFilters.driver_ids) {
        apiFilters.driver_ids = apiFilters.driver_ids.split(',').map(id => id.trim()).filter(id => id);
      }

      if (apiFilters.vehicle_ids) {
        apiFilters.vehicle_ids = apiFilters.vehicle_ids.split(',').map(id => id.trim()).filter(id => id);
      }

      if (apiFilters.trip_status) {
        apiFilters.trip_status = apiFilters.trip_status.split(',').map(status => status.trim()).filter(status => status);
      }

      let blob;
      let filename;

      switch (format) {
        case 'excel':
          blob = await reportService.exportTripReportExcel(apiFilters);
          filename = `trip-report-${new Date().toISOString().split('T')[0]}.xlsx`;
          break;
        default:
          throw new Error('Unsupported export format');
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast({
        title: "Success",
        description: `Report exported as ${format.toUpperCase()} successfully.`
      });
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to export report as ${format.toUpperCase()}: ${error.message || 'Please try again.'}`,
        variant: "destructive"
      });
    } finally {
      setExporting(false);
    }
  };

  const tripStatusOptions = [
    { value: 'PENDING', label: 'Pending' },
    { value: 'ACTIVE', label: 'Active' },
    { value: 'COMPLETED', label: 'Completed' },
  ];

  if (isInitialLoad) {
    return <FullScreenLoader isLoading={isInitialLoad} />;
  }

  return (
    <div className="space-y-6 relative">
      <FullScreenLoader isLoading={exporting} />
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-lg font-semibold tracking-tight text-foreground">Primary Trip Report</h1>
          <p className="text-xs text-muted-foreground">Generate trip performance logs and export vehicle metrics data.</p>
        </div>
        <div className="flex items-center gap-2">
          <CommonButton
            onClick={() => handleGenerateReport(false)}
            disabled={generating}
          >
            Generate Report
          </CommonButton>

          {reports.length > 0 && (
            <CommonButton
              onClick={() => handleExportReport('excel')}
              disabled={exporting}
              variant="outline"
            >
              Export to Excel
            </CommonButton>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 relative">
        <div className={`border border-border p-0.5 rounded-[16px] bg-card w-full transition-all transform relative ${activeDropdown === 'start_date' ? 'z-40' : 'z-10'}`} style={{ boxShadow: '0px 2px 10px 0px hsl(var(--border) / 0.1)' }}>
          <div className="border border-border px-3 py-2 rounded-[16px] bg-background relative">
            <DateTimeInput
              label="Start Date"
              id="start_date"
              value={filters.start_date ? `${filters.start_date}T00:00` : ''}
              onChange={(e) => handleDateChange('start_date', e.target.value)}
              error={errors.start_date}
              required
              maxDate={new Date()}
              onOpenChange={(open) => setActiveDropdown(prev => open ? 'start_date' : (prev === 'start_date' ? null : prev))}
            />
          </div>
        </div>

        <div className={`border border-border p-0.5 rounded-[16px] bg-card w-full transition-all transform relative ${activeDropdown === 'end_date' ? 'z-40' : 'z-10'}`} style={{ boxShadow: '0px 2px 10px 0px hsl(var(--border) / 0.1)' }}>
          <div className="border border-border px-3 py-2 rounded-[16px] bg-background relative">
            <DateTimeInput
              label="End Date"
              id="end_date"
              value={filters.end_date ? `${filters.end_date}T00:00` : ''}
              onChange={(e) => handleDateChange('end_date', e.target.value)}
              error={errors.end_date}
              required
              maxDate={new Date()}
              onOpenChange={(open) => setActiveDropdown(prev => open ? 'end_date' : (prev === 'end_date' ? null : prev))}
            />
          </div>
        </div>

        <div className={`border border-border p-0.5 rounded-[16px] bg-card w-full transition-all transform relative ${activeDropdown === 'trip_status' ? 'z-40' : 'z-10'}`} style={{ boxShadow: '0px 2px 10px 0px hsl(var(--border) / 0.1)' }}>
          <div className="border border-border px-3 py-2 rounded-[16px] bg-background relative">
            <CommonSelect
              label="Trip Status"
              id="trip_status"
              value={filters.trip_status}
              onValueChange={(value) => handleFilterChange('trip_status', value)}
              options={tripStatusOptions}
              placeholder="Select trip status"
              searchable
              onOpenChange={(open) => setActiveDropdown(prev => open ? 'trip_status' : (prev === 'trip_status' ? null : prev))}
            />
          </div>
        </div>

        <div className={`border border-border p-0.5 rounded-[16px] bg-card w-full transition-all transform relative ${activeDropdown === 'port_ids' ? 'z-40' : 'z-10'}`} style={{ boxShadow: '0px 2px 10px 0px hsl(var(--border) / 0.1)' }}>
          <div className="border border-border px-3 py-2 rounded-[16px] bg-background relative">
            <CommonSelect
              label="Port"
              id="port_ids"
              value={filters.port_ids}
              onValueChange={(value) => handleFilterChange('port_ids', value)}
              options={portOptions}
              placeholder="Select port"
              searchable
              onOpenChange={(open) => setActiveDropdown(prev => open ? 'port_ids' : (prev === 'port_ids' ? null : prev))}
            />
          </div>
        </div>

        <div className={`border border-border p-0.5 rounded-[16px] bg-card w-full transition-all transform relative ${activeDropdown === 'transporter_ids' ? 'z-40' : 'z-10'}`} style={{ boxShadow: '0px 2px 10px 0px hsl(var(--border) / 0.1)' }}>
          <div className="border border-border px-3 py-2 rounded-[16px] bg-background relative">
            <CommonSelect
              label="Transporter"
              id="transporter_ids"
              value={filters.transporter_ids}
              onValueChange={(value) => handleFilterChange('transporter_ids', value)}
              options={transporterOptions}
              placeholder="Select transporter"
              searchable
              onOpenChange={(open) => setActiveDropdown(prev => open ? 'transporter_ids' : (prev === 'transporter_ids' ? null : prev))}
            />
          </div>
        </div>

      </div>

      {summary && (
        <Card>
          <CardHeader>
            <CardTitle>Report Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="border p-4 rounded">
                <h3 className="font-semibold">Total Trips</h3>
                <p className="text-2xl">{summary.total_trips || 0}</p>
              </div>
              <div className="border p-4 rounded">
                <h3 className="font-semibold">Total Distance</h3>
                <p className="text-2xl">{summary.total_distance || 0} km</p>
              </div>
              <div className="border p-4 rounded">
                <h3 className="font-semibold">Avg. Trip Duration</h3>
                <p className="text-2xl">{summary.average_trip_duration || 0} hrs</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {reports.length > 0 && (
        <TableBlurLoader isLoading={generating}>
          <div className="border border-border p-0.5 rounded-[16px] bg-card w-full transition-all transform mt-4" style={{ boxShadow: '0px 2px 10px 0px hsl(var(--border) / 0.1)' }}>
            <div className="border border-border py-4 rounded-[16px] bg-background">
              <TripReportTable reports={reports} />
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={Math.ceil(totalReports / pageSize)}
              totalItems={totalReports}
              pageSize={pageSize}
              onPageChange={setPage}
              className="border-t border-border pt-4"
            />
          </div>
        </TableBlurLoader>
      )}

    </div>
  );
};

export default TripReport;